import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { toast } from 'sonner';
import {
  getSuperScheduledCampaigns,
  getSuperLiveMessages,
} from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { Radio, Calendar, Users, Loader2, Repeat } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../lib/utils';
import { formatDistanceToNow, format } from 'date-fns';
import { ptBR } from 'date-fns/locale/pt-BR';

// Polling intervals
const SCHEDULED_REFRESH_MS = 30_000;  // campanhas agendadas mudam pouco
const LIVE_REFRESH_MS = 1_000;        // poll do feed ao vivo
const LIVE_WINDOW_MS = 10_000;        // janela deslizante: msgs somem após 10s
const LIVE_FEED_CAP = 200;            // safety net contra picos altíssimos
const MAX_VISIBLE_ROWS = 20;          // cap visual: ticker mostra só as N mais recentes

// Framer Motion-wrapped TableRow — anima entry/exit suavemente como ticker.
const MotionTableRow = motion(TableRow);

// Statuses considered "final" — message is no longer in flight and is excluded from the feed.
const FINAL_STATUSES = new Set(['delivered', 'read']);

// Color classes for the status badge — pill com fundo + texto tinted.
function statusBadgeClass(status, deliveryStatus) {
  const s = (deliveryStatus || status || '').toLowerCase();
  if (s === 'failed' || s === 'unknown')
    return 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300';
  if (s === 'queued' || s === 'pending')
    return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300';
  if (s === 'sent')
    return 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300';
  return 'bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-gray-300';
}

function statusLabel(status, deliveryStatus) {
  const s = (deliveryStatus || status || '').toLowerCase();
  const map = {
    failed: 'falha',
    unknown: 'desconhecido',
    queued: 'em fila',
    pending: 'pendente',
    sent: 'enviada',
  };
  return map[s] || s || '—';
}

function relativeTime(iso) {
  if (!iso) return '';
  try {
    return formatDistanceToNow(new Date(iso), { addSuffix: true, locale: ptBR });
  } catch {
    return '';
  }
}

function scheduledTime(iso) {
  if (!iso) return '—';
  try {
    return format(new Date(iso), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
  } catch {
    return iso;
  }
}

export default function MessagesLivePage() {
  // Quadro 1: campanhas agendadas
  const [scheduledCampaigns, setScheduledCampaigns] = useState([]);
  const [loadingScheduled, setLoadingScheduled] = useState(true);

  // Quadro 2: feed ao vivo
  const [liveMessages, setLiveMessages] = useState([]);
  const [loadingLive, setLoadingLive] = useState(true);
  const lastAtRef = useRef(null);


  // Trava TODOS os containers que poderiam rolar enquanto essa tela está montada:
  // <html>, <body> e <main> do Layout. Belt-and-suspenders contra scroll de página.
  // useLayoutEffect roda SYNCHRONOUSLY antes do paint, evitando piscada de scroll.
  useLayoutEffect(() => {
    const targets = [
      document.documentElement,
      document.body,
      document.querySelector('main'),
    ].filter(Boolean);
    const saved = targets.map((el) => ({
      el,
      overflow: el.style.overflow,
      overflowX: el.style.overflowX,
      overflowY: el.style.overflowY,
    }));
    targets.forEach((el) => {
      el.style.overflow = 'hidden';
    });
    return () => {
      saved.forEach(({ el, overflow, overflowX, overflowY }) => {
        el.style.overflow = overflow;
        el.style.overflowX = overflowX;
        el.style.overflowY = overflowY;
      });
    };
  }, []);

  // ----- Quadro 1: poll 30s -----
  useEffect(() => {
    let intervalId;
    const fetchScheduled = async () => {
      if (document.visibilityState !== 'visible') return;
      try {
        const res = await getSuperScheduledCampaigns();
        setScheduledCampaigns(res.data.campaigns || []);
      } catch {
        // silent — next tick retries
      } finally {
        setLoadingScheduled(false);
      }
    };
    fetchScheduled();
    intervalId = setInterval(fetchScheduled, SCHEDULED_REFRESH_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') fetchScheduled();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  // ----- Quadro 2: sliding window 10s + poll 1s incremental -----
  // A cada tick:
  //   1. Busca mensagens novas (since=lastSeenAt) — incremental, payload pequeno
  //   2. Mescla com as atuais (dedup por id)
  //   3. Remove TODAS mais velhas que LIVE_WINDOW_MS → "somem" da tela
  //   4. setState → re-render (timestamps relativos atualizam, badge muda)
  // Resultado: a lista é sempre "as últimas 10s de atividade" — não acumula
  // histórico nem trava quando ninguém está enviando.
  useEffect(() => {
    let intervalId;
    const tick = async () => {
      if (document.visibilityState !== 'visible') return;
      let newMsgs = [];
      try {
        const res = await getSuperLiveMessages(lastAtRef.current);
        newMsgs = res.data.messages || [];
        if (newMsgs.length > 0) {
          lastAtRef.current = res.data.last_at || lastAtRef.current;
        }
      } catch {
        // silent — próximo tick retenta
      } finally {
        setLoadingLive(false);
      }
      const cutoff = Date.now() - LIVE_WINDOW_MS;
      setLiveMessages((prev) => {
        const byId = new Map();
        for (const m of prev) byId.set(m.id, m);
        for (const m of newMsgs) byId.set(m.id, m);
        return Array.from(byId.values())
          .filter((m) => {
            if (!m.created_at) return false;
            if (new Date(m.created_at).getTime() <= cutoff) return false;
            // "Em vôo" apenas: descarta msgs em estados finais (entregue/lida)
            const s = (m.delivery_status || m.status || '').toLowerCase();
            if (FINAL_STATUSES.has(s)) return false;
            return true;
          })
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
          .slice(0, LIVE_FEED_CAP);
      });
    };
    tick();
    intervalId = setInterval(tick, LIVE_REFRESH_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') tick();
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, []);

  const liveCount = liveMessages.length;
  const liveCountLabel = liveCount === 1 ? '1 mensagem' : `${liveCount} mensagens`;

  return (
    <>
      {/* Esconde TODAS as scrollbars dentro desta tela (incluindo o wrapper
          interno overflow-auto do componente Table do shadcn), mantendo o
          scroll funcional. Escopado em .messages-live-root para não afetar
          o resto do app. Removido automaticamente pelo React no unmount. */}
      <style>{`
        .messages-live-root * {
          scrollbar-width: none !important;          /* Firefox */
          -ms-overflow-style: none !important;        /* Edge legado */
        }
        .messages-live-root *::-webkit-scrollbar {
          display: none !important;                   /* Chrome/Safari/Edge */
          width: 0 !important;
          height: 0 !important;
        }
      `}</style>
    <div className="messages-live-root grid grid-cols-1 lg:grid-cols-2 gap-4 overflow-x-hidden">
      {/* Quadro 1: Campanhas Agendadas */}
      <Card className="flex flex-col overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="w-5 h-5" />
            Campanhas Agendadas
          </CardTitle>
          <CardDescription>
            Campanhas com envio programado para o futuro, em todos os tenants ({scheduledCampaigns.length}).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <Table containerClassName="h-[650px] overflow-x-hidden scrollbar-hidden">
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead>Administrador</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Campanha</TableHead>
                  <TableHead className="text-right pr-6">Contatos</TableHead>
                  <TableHead>Agendada para</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingScheduled ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : scheduledCampaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6 text-muted-foreground text-sm">
                      Nenhuma campanha agendada no momento
                    </TableCell>
                  </TableRow>
                ) : (
                  scheduledCampaigns.map((c) => (
                    <TableRow key={c.id} className="hover:bg-primary/5">
                      <TableCell className="py-2 text-sm">
                        {c.tenant_name || <span className="text-muted-foreground italic">—</span>}
                      </TableCell>
                      <TableCell className="py-2 text-sm">
                        {c.admin_name || c.admin_email || '—'}
                      </TableCell>
                      <TableCell className="py-2 font-medium">
                        <div className="flex items-center gap-1.5">
                          <span>{c.name}</span>
                          {c.kind === 'recurring' && (
                            <span
                              className="inline-flex items-center gap-1 rounded-full px-1.5 py-0.5 text-[11px] font-medium bg-indigo-100 text-indigo-800 dark:bg-indigo-900/40 dark:text-indigo-300 shrink-0"
                              title="Agendamento recorrente"
                            >
                              <Repeat className="w-3 h-3" />
                              {c.recurrence_label}
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="py-2 text-right text-sm tabular-nums pr-6">
                        <span
                          className="inline-flex items-center gap-1"
                          title={c.kind === 'recurring' ? 'Contatos na próxima rodada (grupo reavaliado a cada envio)' : undefined}
                        >
                          <Users className="w-3.5 h-3.5 text-muted-foreground" />
                          {c.total_contacts}
                        </span>
                      </TableCell>
                      <TableCell className="py-2 text-sm">{scheduledTime(c.scheduled_at)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
        </CardContent>
      </Card>

      {/* Quadro 2: Mensagens Ao Vivo */}
      <Card className="flex flex-col relative overflow-hidden">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Radio className="w-5 h-5" />
            Mensagens Sendo Enviadas
          </CardTitle>
          <CardDescription>
            Envios em curso neste momento (mensagens somem ao concluir).
          </CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          {/* Ticker-style: fixed height, no scrollbar ever, max N rows visible.
              Rows enter from top, others slide down, oldest exits at bottom. */}
          <div className="h-[650px] overflow-hidden w-full scrollbar-hidden">
            <Table>
              <TableHeader className="sticky top-0 bg-card z-10">
                <TableRow>
                  <TableHead>Administrador</TableHead>
                  <TableHead>Usuário</TableHead>
                  <TableHead>Destinatário</TableHead>
                  <TableHead>Origem</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingLive ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-6">
                      <Loader2 className="w-5 h-5 animate-spin mx-auto text-muted-foreground" />
                    </TableCell>
                  </TableRow>
                ) : liveMessages.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-muted-foreground text-sm">
                      Nenhum envio no momento
                    </TableCell>
                  </TableRow>
                ) : (
                  <AnimatePresence initial={false} mode="popLayout">
                    {liveMessages.slice(0, MAX_VISIBLE_ROWS).map((m) => (
                      <MotionTableRow
                        key={m.id}
                        layout
                        initial={{ opacity: 0, y: -20 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: 16, transition: { duration: 0.2 } }}
                        transition={{ duration: 0.3, ease: 'easeOut' }}
                        className="hover:bg-primary/5"
                      >
                        <TableCell className="py-2 text-sm">
                          {m.tenant_name || <span className="text-muted-foreground italic">—</span>}
                        </TableCell>
                        <TableCell className="py-2 text-sm">
                          {m.sent_by_name || <span className="text-muted-foreground italic">—</span>}
                        </TableCell>
                        <TableCell className="py-2 text-sm">
                          {m.contact_name ? (
                            <span>
                              {m.contact_name}{' '}
                              <span className="text-muted-foreground text-xs">{m.phone}</span>
                            </span>
                          ) : (
                            <span className="font-mono text-xs">{m.phone}</span>
                          )}
                        </TableCell>
                        <TableCell className="py-2 text-xs text-muted-foreground">
                          {m.is_campaign ? 'Campanha' : m.is_send_job ? 'Envio direto' : '—'}
                        </TableCell>
                        <TableCell className="py-2">
                          <span
                            className={cn(
                              'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium',
                              statusBadgeClass(m.status, m.delivery_status)
                            )}
                          >
                            {statusLabel(m.status, m.delivery_status)}
                          </span>
                        </TableCell>
                      </MotionTableRow>
                    ))}
                  </AnimatePresence>
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
        {/* Floating live counter — appears only when there's actual activity */}
        {liveCount > 0 && (
          <div
            className={cn(
              'absolute bottom-4 right-4 z-10',
              'inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full',
              'text-sm font-semibold tabular-nums',
              'bg-card border border-green-300 dark:border-green-700',
              'shadow-lg',
              'text-green-900 dark:text-green-100',
              'animate-in fade-in slide-in-from-bottom-2 duration-300'
            )}
            aria-live="polite"
          >
            <span className="inline-block h-2 w-2 rounded-full bg-green-500 shadow-[0_0_6px_rgba(34,197,94,0.8)] animate-pulse" />
            {liveCountLabel}
          </div>
        )}
      </Card>
    </div>
    </>
  );
}
